/**
 * 문의하기 — 구글 폼(Support Center) 직접 제출.
 *
 * 예전에는 설정 > 고객센터가 Device.openSupportCenter() 로 폼을 외부 브라우저에 띄웠다.
 * 앱 밖으로 나가고, 영어 한 가지뿐이고, 유저에게 보일 이유가 없는 Account/Device/버전
 * 칸이 화면 절반을 먹었다. 지금은 우리 UI 로 받아 formResponse 로 직접 POST 한다.
 */

const FORM_ID = '1FAIpQLSdCnWd2DrlH6kHcROECZkHFRlwoY7gWt91F7SNwGCDjobQ9Gw';

const FORM_ACTION = `https://docs.google.com/forms/d/e/${FORM_ID}/formResponse`;

/**
 * 폼 문항 ↔ entry id. (Support Center - 2025 폼, 2026-08-30 확인)
 *
 * ⚠️⚠️ **여섯 문항이 전부 필수다.** 하나라도 빈 문자열로 보내면 폼이 400 으로 거부한다
 *    (2026-08-31 확인). 유저에게 안 보이는 Account/Device/버전 칸도 마찬가지라,
 *    값을 못 구했을 때 빈 값을 그냥 보내면 문의 전체가 조용히 사라진다 — `required()` 참고.
 * ⚠️ 폼에서 질문을 지우고 다시 만들면 id 가 바뀐다. 바뀐 값은 viewform HTML 의
 *    `FB_PUBLIC_LOAD_DATA_` 배열에서 확인한다(문항마다 `[[id, 보기, 필수여부]]`).
 * ⚠️ `emailAddress` 는 구글 기본 "이메일 주소 수집" 필드라 질문 배열(FB_PUBLIC_LOAD_DATA_)
 *    에도, 렌더된 HTML 에도 질문으로 안 나온다. 그래도 이 파라미터로 채울 수 있고
 *    응답 시트의 `Email Address` 열로 들어간다 — 2026-08-31 실제 제출로 확인.
 *    (viewform 의 이메일 input 이 `disabled` 로 렌더되는 건 비로그인 상태의 표시일 뿐이라
 *     수집 모드를 판별하는 근거가 못 된다. 이비자 폼도 똑같이 렌더되지만 잘 들어간다.)
 */
const ENTRY = {
  email: 'emailAddress',
  category: 'entry.471895397',
  description: 'entry.266494490',
  account: 'entry.2020601225',
  device: 'entry.303159202',
  appVersion: 'entry.919677359',
  swVersion: 'entry.291820744',
} as const;

/**
 * 카테고리 전송값.
 *
 * ⚠️⚠️ 이 문자열은 **절대 번역하지 않는다.** 폼의 드롭다운 문항이라 한 글자만 달라도
 * 제출이 거부되고, `submitInquiry` 가 no-cors 라 그 거부가 성공과 구분되지 않는다.
 * 게다가 admin 세 곳이 이 값을 그대로 읽는다 —
 *   - 무회신 판정: admin/aggregator/csConfig.js (noReplyCategories)
 *   - Slack 첨부 색상: admin/aggregator/services/CsSlackService.js (CATEGORY_COLOR_MAP)
 * 화면에 보이는 라벨은 `labelKey` 로 따로 그린다.
 */
export const INQUIRY_CATEGORIES = [
  { id: 'payment', value: 'Payment Issue', labelKey: 'inquiry_cat_payment', group: 'payment' },
  // 'Report a bug' 하나로 받던 자리를 증상별로 펼친 항목. 폼 드롭다운에는 증상 값이 없어
  // 전송값은 다 같고, 고른 증상은 `symptom` 으로 description 첫 줄에 남긴다.
  // 순서는 실제 접수 건수 순 — 로그인 51, 기기 미지원 33, 번역 28,
  // 카메라 10, 마이크 10, 느림 5, 매칭 5 (cs.threads 전 기간, 2026-09-01 집계).
  //
  // '앱이 열리지 않아요'(appopen) 는 뺐다. 이 화면은 앱 안에만 있어서 앱이 정말 안 열리면
  // 여기까지 못 온다 — 고른 23명 전원이 홈 탭까지 갔고 문의 앞뒤 6시간 안에 로그인 기록이
  // 있었다(2026-09-04 집계). 가리킬 수 있는 상태가 없으니 아무 불편에나 골라, 30건 중 17건이
  // 본문 없이 자판만 두드린 접수였다. 대신 실제로 겪을 수 있는 상태로 나눈다 — 무한 로딩은
  // 이미 slow('앱이 느리거나 로딩만 계속돼요') 가, 로그인 후 진행 불가는 login 이 받는다.
  // 앱을 아예 못 여는 유저는 notices/es-not-supported.html 의 폼 링크로 따로 들어온다.
  { id: 'login', value: 'Report a bug', labelKey: 'inquiry_bug_login', symptom: 'Login', group: 'app' },
  { id: 'screen', value: 'Report a bug', labelKey: 'inquiry_bug_screen', symptom: 'A screen does not open', group: 'app' },
  { id: 'crash', value: 'Report a bug', labelKey: 'inquiry_bug_crash', symptom: 'Closes by itself', group: 'app' },
  { id: 'device', value: 'Report a bug', labelKey: 'inquiry_bug_device', symptom: 'Device not supported', group: 'app' },
  { id: 'translate', value: 'Report a bug', labelKey: 'inquiry_bug_translate', symptom: 'Translation', group: 'app' },
  { id: 'video', value: 'Report a bug', labelKey: 'inquiry_bug_video', symptom: 'Camera', group: 'app' },
  { id: 'audio', value: 'Report a bug', labelKey: 'inquiry_bug_audio', symptom: 'Microphone', group: 'app' },
  { id: 'slow', value: 'Report a bug', labelKey: 'inquiry_bug_slow', symptom: 'Slow', group: 'app' },
  { id: 'match', value: 'Report a bug', labelKey: 'inquiry_bug_match', symptom: 'Matching', group: 'app' },
  { id: 'blocked', value: 'Suspended(blocked) account', labelKey: 'inquiry_cat_blocked', group: 'account' },
  { id: 'account', value: 'General inquiries', labelKey: 'inquiry_cat_account', group: 'account' },
  { id: 'other', value: 'Others', labelKey: 'inquiry_cat_other', group: 'etc' },
] as const;

/**
 * 목록을 묶는 단위. 항목이 12개라 한 덩어리로 두면 고르기 어렵다 —
 * 설정 화면이 쓰는 `ul-wrapper` + `ul-title` 과 같은 위계다.
 * 마지막 묶음은 항목이 하나뿐이라 제목을 달지 않는다.
 *
 * '계정' 을 '앱이 안 될 때' 앞에 둔다. 정지된 유저에게는 실제로 앱이 안 열리는 게 맞아서
 * 증상 항목을 먼저 보면 거기로 들어가 버린다 — 배포 첫날 정지 문의 10건이 전부
 * 'Report a bug' 의 앱 안 열림·로그인·카메라·마이크·느림으로 접수됐고
 * `blocked` 로 들어온 건 0건이었다(2026-09-02 집계). 증상을 고르기 전에 갈라야 한다.
 */
export const INQUIRY_GROUPS = [
  { id: 'payment', titleKey: 'inquiry_group_payment' },
  { id: 'account', titleKey: 'inquiry_group_account' },
  // 증상 9개는 첫 화면에 다 펴면 목록이 12줄이 된다. 이 묶음만 한 단계 들어가게 해
  // 첫 화면을 5줄로 줄인다. 항목이 하나둘뿐인 묶음까지 접으면 오히려 손이 더 간다.
  { id: 'app', titleKey: 'inquiry_group_app', drilldown: true },
  { id: 'etc', titleKey: null },
] as const;

export type InquiryCategoryId = (typeof INQUIRY_CATEGORIES)[number]['id'];

export interface FaqItem {
  /** 문항 제목 리소스 키 */
  qKey: string;
  /** 답변 본문 리소스 키. 여러 개면 줄바꿈으로 잇는다. */
  aKeys: string[];
}

/**
 * 카테고리를 고른 직후 먼저 보여줄 문항.
 * 답변 본문 대부분은 알림 탭 FAQ 가 쓰던 `qna_contents_*` 를 그대로 재사용한다 —
 * 이미 17개 로케일 번역이 붙어 있다.
 */
export const INQUIRY_FAQ: Partial<Record<InquiryCategoryId, FaqItem[]>> = {
  payment: [
    { qKey: 'qna_contents_no_coins', aKeys: ['qna_contents_restart_device', 'qna_contents_check_network'] },
    { qKey: 'inquiry_faq_refund_q', aKeys: ['inquiry_faq_refund_a'] },
  ],
  blocked: [
    {
      qKey: 'qna_contents_account_blocked',
      aKeys: ['qna_contents_inappropriate_cotents', 'qna_contents_inappropriate_profile', 'qna_contents_under_age'],
    },
    { qKey: 'inquiry_faq_unban_q', aKeys: ['inquiry_faq_unban_a'] },
  ],
  account: [
    { qKey: 'qna_contents_delete_account', aKeys: ['qna_contents_click_my_page', 'qna_contents_lose_all_data'] },
    { qKey: 'qna_contents_guidelines', aKeys: ['inquiry_faq_guide_a'] },
  ],
  // 증상 항목은 앱이 이미 그 상황에서 띄우는 문구를 그대로 가져다 쓴다. 그런 문구가 없는
  // 증상(앱 안 열림·마이크·느림·매칭)은 지어내지 않고 FAQ 없이 바로 본문으로 넘어간다.
  login: [{ qKey: 'inquiry_bug_login', aKeys: ['kickout_logout'] }],
  device: [{ qKey: 'inquiry_bug_device', aKeys: ['app_unavailable_body'] }],
  translate: [{ qKey: 'inquiry_bug_translate', aKeys: ['tr_lang_explain_title', 'tr_lang_explain_sub4'] }],
  video: [{ qKey: 'inquiry_bug_video', aKeys: ['camera_permission_request'] }],
};

export const DETAIL_MAX = 3000;

/**
 * 본문 최소 길이. 이보다 짧으면 전송 버튼이 잠긴다.
 *
 * 길이만으로는 장난을 다 거를 수 없다 — 'Hdjcjdjdjdkdjkdkdkd' 는 19자로 통과하고
 * '코인 미수령'·'마이크 안됨' 은 6자짜리 멀쩡한 제보다. 그래서 10자가 아니라 6자로 둔다.
 * 배포 첫날 접수 66건 중 'Xxx' 'uu' 'hu' 'Olz' 'Jade' 처럼 CS 가 읽을 게 없는 건이
 * 이 선에서 걸린다(2026-09-02 집계).
 */
export const DETAIL_MIN = 6;

const SUBMIT_TIMEOUT_MS = 15000;

/**
 * 실제로 폼에 나가는 간격. 한 계정에서 이 시간 안에 두 번째 접수는 전송하지 않는다.
 *
 * 내용을 보지 않는다. 09-03~09-10 접수 982건에서 10분 안에 다시 쓴 130쌍을 재보면,
 * 정규화 후 같은 건 16쌍·어절 유사도 0.5 이상이 16쌍뿐이고 나머지 98쌍(75%)은 문장이 안 겹쳤다
 * (`Koin gada` → `Koin 2 kali beli ga masuk` → `Balikin duit gw 2 kali woy` 가 유사도 0.14~0.20 이다).
 * 같은 사건을 매번 다르게 쓰므로 어떤 임계값으로도 못 가른다.
 *
 * 카테고리로도 나누지 않는다. 같은 130쌍 중 81쌍(62%)이 서로 다른 카테고리였는데,
 * 밴 문의를 `Report a bug` 과 `Others` 로 나눠 쓰는 식이라 같은 사건이다.
 */
const SEND_INTERVAL_MS = 60 * 60 * 1000;

const LAST_SUBMIT_KEY = 'inquiry_last_submit';

/**
 * 마지막 전송 시각을 기억한다. localStorage 는 구형 WebView·사파리 프라이빗에서 접근만으로도
 * 던질 수 있어 전부 감싼다 — 기억을 못 하면 중복이 한 번 더 들어올 뿐, 접수는 막지 않는다.
 *
 * ⚠️ 재설치·시크릿 창에서는 기억이 사라져 간격 제한이 안 걸린다. 확실히 막으려면 계정 단위로
 *    서버가 세야 하는데, 접수가 구글 폼 직행이라 서버는 제출 자체를 못 본다.
 */
function readLastSubmit(): { at: number } | null {
  try {
    const raw = localStorage.getItem(LAST_SUBMIT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.at === 'number' ? { at: parsed.at } : null;
  } catch {
    return null;
  }
}

function rememberSubmit(): void {
  try {
    localStorage.setItem(LAST_SUBMIT_KEY, JSON.stringify({ at: Date.now() }));
  } catch {
    // 무시 — 기억을 못 해도 접수 자체는 그대로 나간다.
  }
}

function forgetSubmit(): void {
  try {
    localStorage.removeItem(LAST_SUBMIT_KEY);
  } catch {
    // 무시
  }
}

/** 아주 느슨한 형식 검사 — 오타로 답장이 반송되는 것만 막는다. */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

/** 필수 문항이 빈 값이면 폼이 400 으로 거부한다. 값을 못 구해도 자리는 채운다. */
function required(value: string | number | undefined | null): string {
  const text = String(value ?? '').trim();
  return text || 'unknown';
}

interface SystemInfo {
  model?: string;
  release?: string;
}

/**
 * ⚠️ `Device.getSystemInfo` 는 네이티브 콜백이라 안 올 수도 있다.
 * 전송이 영영 안 끝나는 것보다 기기 정보 두 칸이 비는 게 낫다.
 */
function loadSystemInfo(): Promise<SystemInfo> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({}), 3000);
    try {
      Device.getSystemInfo((jsonstr: string) => {
        clearTimeout(timer);
        try {
          resolve((JSON.parse(jsonstr) || {}) as SystemInfo);
        } catch {
          resolve({});
        }
      });
    } catch {
      clearTimeout(timer);
      resolve({});
    }
  });
}

/**
 * 기기 정보 두 칸(Device, SW version).
 *
 * ⚠️ 웹에는 네이티브 브리지가 없어 `Device.getSystemInfo` 콜백이 영영 안 온다.
 *    그러면 위 타임아웃이 빈 값을 돌려주고, 필수 문항이라 폼이 400 으로 거부한다.
 *    웹은 브라우저명·버전으로 채운다 — admin 의 device 버킷팅도 웹을 소문자
 *    브라우저명으로 읽는다(admin/routes/api/cs.js 의 toDeviceBucket).
 */
async function loadDeviceInfo(): Promise<{ device: string; swVersion: string }> {
  if (Device.name === 'web') {
    return { device: required(getBrowserName()), swVersion: required(getBrowserVersion()) };
  }

  const { model, release } = await loadSystemInfo();
  return { device: required(model), swVersion: required(release) };
}

/**
 * 문의 화면에서 보여주는 코인 충전 내역 한 건.
 * 출처가 둘이다 — `notification` 컬렉션의 purchaseCoin 문서와, Play 에만 있는 미소비 구매.
 */
export interface CoinOrder {
  /** 목록 식별자. PENDING 구매는 주문번호가 아직 없어 purchaseToken 이 들어간다. */
  key: string;
  /** 결제사 주문번호. PENDING 구매는 빈 문자열이다. */
  orderId: string;
  /**
   * 'paid' 만 지급 완료. billingComplete·notPaid 는 결제 콜백만 받고 지급이 끝나지 않은 상태다.
   * Play 에서만 온 건은 'pending'(결제 승인 대기) 또는 'notConsumed'(승인됐고 지급 대기)다.
   */
  status: string;
  amount: number;
  /** 지급 시각(없으면 생성 시각). Play 에서 온 건은 purchaseTime. */
  at: number;
  paid: boolean;
  /** Play 에서 온 건만 채운다. 코인 상품을 가리는 데 쓴다. */
  productId?: string;
}

/** Play 에서 온 건인지. 화면은 이걸 '실패' 가 아니라 '처리중' 으로 그려야 한다. */
export function isInProgress(order: CoinOrder): boolean {
  return order.status === 'pending' || order.status === 'notConsumed';
}

/** 알림>구매 탭에는 코인 말고도 럭키박스·타임버프가 섞여 있어 넉넉히 받아 코인만 걸러낸다. */
const ORDER_FETCH_COUNT = 30;

/** 화면에 펴 두는 건수. 더 늘리면 FAQ 를 밀어내 정작 답이 안 보인다. */
export const ORDER_SHOW_MAX = 5;

/**
 * ⚠️ Meteor 콜백이라 안 올 수도 있다. 문의 화면이 목록을 기다리다 멈추는 것보다
 *    목록 없이 FAQ 로 넘어가는 게 낫다.
 */
const ORDER_TIMEOUT_MS = 5000;
/** 번역 상태 조회. 못 읽어도 접수는 그대로 진행한다. */
const TRANSLATE_TIMEOUT_MS = 3000;

/**
 * 내 코인 충전 내역.
 *
 * uid 는 넘기지 않는다 — 서버가 세션에서 잡는다(알림 탭이 쓰는 것과 같은 경로).
 * 필터는 알림 탭이 실제로 쓰는 `parentCategory` 값만 쓰고, purchaseCoin 추림은
 * 클라에서 한다. 서버가 임의 필드를 받아주는지 확인된 바 없어서다.
 */
function loadNotificationOrders(): Promise<CoinOrder[]> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve([]), ORDER_TIMEOUT_MS);
    const done = (list: CoinOrder[]) => {
      clearTimeout(timer);
      resolve(list);
    };

    try {
      NotificationDao.getList({ parentCategory: 'purchase' }, { count: ORDER_FETCH_COUNT }, (error, result) => {
        if (error || !result || !Array.isArray(result.list)) {
          done([]);
          return;
        }
        done(shapeCoinOrders(result.list));
      });
    } catch {
      done([]);
    }
  });
}

function shapeCoinOrders(raw: unknown[]): CoinOrder[] {
  return raw
    .map((item) => item as Record<string, unknown>)
    .filter((item) => item?.subtype === 'purchaseCoin')
    .map((item) => {
      const status = String(item.status ?? '');
      return {
        key: String(item._id ?? ''),
        orderId: String(item._id ?? ''),
        status,
        amount: Number(item.amount ?? 0),
        at: new Date(String(item.paidAt ?? item.createdTime ?? '')).getTime(),
        paid: status === 'paid',
      };
    })
    .filter((order) => order.orderId && Number.isFinite(order.at) && order.at > 0);
}

/**
 * Play 가 아직 소비 처리하지 않은 인앱 구매.
 *
 * 결제 후 Play 가 결제를 확정하기까지 몇 분 걸리는 나라가 있다(최근 7일 BR·ID 는 다섯 건 중
 * 한 건이 2분 초과). 그 구간은 notification·결제 큐·원장 어디에도 안 남아 유저에게
 * "결제했는데 기록도 없다" 로 보인다. 기기의 Play 클라이언트만 아는 구간이라 직접 묻는다.
 *
 * 브리지는 조회 실패·미지원(iOS·web·구버전 안드)에 null 을 준다. 빈 배열과 같이 다뤄도
 * 되는 자리다 — 알림 쪽 목록과 합치기 때문에 아무것도 안 보태는 것으로 끝난다.
 */
function loadNotConsumedPurchases(): Promise<CoinOrder[]> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve([]), ORDER_TIMEOUT_MS);
    const done = (list: CoinOrder[]) => {
      clearTimeout(timer);
      resolve(list);
    };

    try {
      Device.getNotConsumedOnetimeProducts((json: string | null) => {
        if (!json) {
          done([]);
          return;
        }
        try {
          const parsed = JSON.parse(json);
          done(Array.isArray(parsed) ? shapePlayPurchases(parsed) : []);
        } catch {
          done([]);
        }
      });
    } catch {
      done([]);
    }
  });
}

/** Play originalJson 의 purchaseState. getPurchaseState() 값과 달라 0 이 구매 완료, 4 가 승인 대기다. */
const PLAY_JSON_STATE_PENDING = 4;

/** 코인 상품 productId 접두사. PageShop.getCoin 이 쓰는 판정과 같다. */
const COIN_PRODUCT_PREFIX = 'ccoin_';

function shapePlayPurchases(raw: unknown[]): CoinOrder[] {
  return raw
    .map((item) => item as Record<string, unknown>)
    .map((item) => {
      const orderId = String(item.orderId ?? '');
      const productId = String(item.productId ?? '');
      return {
        key: orderId || String(item.purchaseToken ?? ''),
        orderId,
        status: Number(item.purchaseState) === PLAY_JSON_STATE_PENDING ? 'pending' : 'notConsumed',
        // ⚠️ 상점 상품 목록이 아직 안 실렸으면 0 이 나온다. 처리중 행은 코인 수를 안 그리니
        //    표시에는 지장 없지만, 이 값으로 코인 상품을 가리면 멀쩡한 건이 통째로 사라진다.
        amount: Number(PageShop.getPromoCoin(productId) ?? 0),
        productId,
        at: Number(item.purchaseTime ?? 0),
        paid: false,
      };
    })
    // 코인 상품만 남긴다. 상점이 로드됐는지와 무관한 기준을 쓴다(PageShop.getCoin 과 같은 접두사 판정).
    .filter((order) => order.key && order.productId.startsWith(COIN_PRODUCT_PREFIX) && Number.isFinite(order.at) && order.at > 0);
}

/**
 * 화면에 뿌릴 충전 내역. 알림 기록과 Play 미소비 구매를 합친다.
 * 같은 주문이 양쪽에 있으면 알림 쪽(지급 결과를 아는 쪽)을 남긴다.
 */
export function loadRecentCoinOrders(): Promise<CoinOrder[]> {
  return Promise.all([loadNotificationOrders(), loadNotConsumedPurchases()]).then(([notified, play]) => {
    const seen = new Set(notified.map((order) => order.key));
    return notified
      .concat(play.filter((order) => !seen.has(order.key)))
      .sort((a, b) => b.at - a.at)
      .slice(0, ORDER_SHOW_MAX);
  });
}

/**
 * 온디바이스 번역(ML Kit) 상태. 조회에 실패하거나 웹이면 null.
 *
 * 언어팩 다운로드는 실패율이 17%(2026-08~09 14일, 시도 101,595건) 인데, 실패해도 화면에
 * 흔적이 남지 않아 CS 는 "번역이 안 된다" 는 문장만 받는다. 접수 시점에 상태를 같이 실어
 * 두면 그 자리에서 갈린다 — 모델이 없으면 다운로드 실패고, 있으면 다른 원인이다.
 */
function loadTranslateState(): Promise<{ lang: string | null; models: string[]; use: string; downloading: boolean } | null> {
  return new Promise((resolve) => {
    if (Device.name === 'web' || typeof MLKitTranslator === 'undefined') {
      resolve(null);
      return;
    }

    const timer = setTimeout(() => resolve(null), TRANSLATE_TIMEOUT_MS);
    const done = (models: string[]) => {
      clearTimeout(timer);
      resolve({
        lang: MLKitTranslator.getMyLanguage(),
        models,
        use: MLKitTranslator.getUseOption(),
        downloading: MLKitTranslator.isDownloading(),
      });
    };

    try {
      Device.getTranslatorDownloadedModel((listStr: string | null) => {
        try {
          const parsed = JSON.parse(listStr || '[]');
          done(Array.isArray(parsed) ? parsed.map(String) : []);
        } catch {
          done([]);
        }
      });
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

/**
 * 번역이 정상일 때는 아무것도 안 남긴다 — 이상할 때만 CS 가 읽을 게 생긴다.
 * 정상 = 설정된 언어의 모델이 실제로 깔려 있음.
 */
function buildTranslateLine(state: Awaited<ReturnType<typeof loadTranslateState>>): string {
  if (!state) return '';
  const { lang, models, use, downloading } = state;
  if (lang && models.includes(lang)) return '';

  const parts = [`lang ${lang || 'none'}`, `models ${models.length ? models.join(',') : 'none'}`, `use ${use}`];
  if (downloading) parts.push('downloading');
  return `Translate: ${parts.join(' · ')}`;
}

/**
 * CS 가 먼저 확인하는 두 값. 유저 화면에는 안 보이고 본문 첫 줄에만 실린다.
 *
 * 국가는 결제 채널·지역 이슈를 가르는 첫 축이고, 코인 잔액은 "결제했는데 안 들어왔다"
 * 문의(전체의 절반)에서 바로 대조된다. 값을 못 구하면 그 자리를 비운다 —
 * 폼 필수 문항이 아니라 본문 안이라 빈 값이어도 제출은 통과한다.
 */
function buildContext(): string {
  const parts = [];

  const country = String(myinfo?.country ?? '').trim();
  if (country) parts.push(country);

  const coin = MyDataManager?.getCoin?.();
  if (typeof coin === 'number' && Number.isFinite(coin)) parts.push(`coin ${coin}`);

  return parts.join(' · ');
}

/** CS 가 한 줄로 읽는 충전 내역 요약. 지목한 건이 있으면 주문번호까지 적는다. */
function buildOrderLine(orders: CoinOrder[], picked: CoinOrder | null): string {
  if (!orders.length) return 'Orders: none';

  const unpaid = orders.filter((order) => !order.paid).length;
  const parts = [`shown ${orders.length}`, `unpaid ${unpaid}`];
  const inProgress = orders.filter(isInProgress).length;
  if (inProgress) parts.push(`inProgress ${inProgress}`);
  if (picked) {
    // PENDING 건은 주문번호가 없어 purchaseToken 이 나간다. CS 가 Play 에 물을 수 있는 유일한 키다.
    parts.push(`picked ${picked.orderId || picked.key} ${picked.status} ${picked.amount} ${new Date(picked.at).toISOString()}`);
  }
  return `Orders: ${parts.join(' · ')}`;
}

/**
 * 유저가 쓴 본문 앞에 CS 가 필요로 하는 것을 붙인다.
 * 폼에 칸을 새로 만들지 않아 응답 시트 컬럼과 admin 파이프라인이 그대로 유지된다.
 *
 * 응답 언어는 시트에 열이 따로 없다. CS 가 어느 말로 답할지 알아야 해서 첫 줄에 남긴다
 * (본문 자체는 CsTranslatePipeline 이 한/영으로 번역해 준다).
 */
function buildDescription(input: {
  locale: string;
  symptom?: string;
  detail: string;
  orders?: CoinOrder[];
  pickedOrder?: CoinOrder | null;
  translateLine?: string;
}): string {
  const context = buildContext();
  const lines = [context ? `[${input.locale}] ${context}` : `[${input.locale}]`];

  // 코인 문의에서 충전 내역을 보여준 경우에만 남긴다. 유저가 어느 건을 지목했는지와,
  // 지급 완료 내역을 보고도 문의했는지가 같이 남아야 안내가 듣는지 판정할 수 있다.
  if (input.orders) {
    lines.push(buildOrderLine(input.orders, input.pickedOrder ?? null));
  }

  // 증상 항목은 전송값이 다 'Report a bug' 이라 시트의 Category 열만으로는 구분되지 않는다.
  // 본문에 이름만 적고 마는 문의가 많아, 고른 증상만이라도 남겨야 CS 가 읽을 게 생긴다.
  if (input.symptom) {
    lines.push(`Symptom: ${input.symptom}`);
  }

  // 번역이 깨진 상태일 때만 실린다. 카테고리를 안 가리는 이유는, 번역 문의가 'Report a bug'
  // 밖(Others·General inquiries)으로도 들어오기 때문이다.
  if (input.translateLine) {
    lines.push(input.translateLine);
  }

  lines.push('', input.detail.trim().slice(0, DETAIL_MAX));
  return lines.join('\n');
}

/**
 * 폼 제출.
 *
 * ⚠️⚠️ **성공 여부를 알 수 없다.** 크로스 오리진이라 `no-cors` 로 보내고, 그러면 응답이
 * opaque 라 200 과 400 이 구분되지 않는다. 여기서 실패로 잡히는 건 네트워크 자체가
 * 죽었거나 타임아웃일 때뿐이다. 그래서 값이 하나라도 틀리면 유저에겐 "접수됨" 으로
 * 보이면서 시트에는 아무것도 안 남는다.
 *
 * 확인 기록 (2026-08-31):
 *   - 전 필드를 채워 1건 제출 → HTTP 200, 응답 시트 `Form Responses 3` 에 행 생성.
 *     `emailAddress` 포함 8개 필드가 모두 제자리 컬럼에 들어갔다.
 *   - `emailAddress`/Account/Device/Application version/SW version 을 하나씩 빈 값으로
 *     보내면 전부 400 → 전부 필수. 실제로 웹에서 SW version 이 비어 나가 400 이 났었다.
 *   - 카테고리 5개 전송값은 viewform 의 드롭다운 문자열과 정확히 일치함을 대조.
 * 폼 문항이나 카테고리를 건드리면 이 확인을 다시 해야 한다. 다만 운영 폼에 테스트를
 * 쏘면 Slack 알림이 울리고 접수 확인 메일이 실제로 발송되며 cs.threads 에 행이 쌓이므로,
 * 가능하면 폼 사본(복제해도 entry id 는 유지된다)에서 확인한다.
 */
export async function submitInquiry(input: {
  category: InquiryCategoryId;
  detail: string;
  email: string;
  locale: string;
  orders?: CoinOrder[];
  pickedOrder?: CoinOrder | null;
}): Promise<void> {
  const category = INQUIRY_CATEGORIES.find((item) => item.id === input.category);
  if (!category) throw new Error(`unknown inquiry category: ${input.category}`);

  // 직전 전송에서 아직 한 시간이 안 지났으면 보내지 않는다. 항목도 본문도 보지 않는다.
  // 화면은 평소대로 접수 완료로 넘어간다 — 먼저 낸 문의가 열려 있어 CS 가 그 계정을 이미 보고 있고,
  // 여기서 막았다고 알리면 유저는 접수가 안 됐다고 읽어 결국 다시 쓴다.
  const last = readLastSubmit();
  if (last && Date.now() - last.at < SEND_INTERVAL_MS) return;

  const [{ device, swVersion }, translateState] = await Promise.all([loadDeviceInfo(), loadTranslateState()]);

  const body = new URLSearchParams({
    [ENTRY.email]: input.email.trim(),
    [ENTRY.category]: category.value,
    [ENTRY.description]: buildDescription({
      ...input,
      symptom: 'symptom' in category ? category.symptom : undefined,
      translateLine: buildTranslateLine(translateState),
    }),
    [ENTRY.account]: required(myinfo?.fbUid),
    [ENTRY.device]: device,
    [ENTRY.appVersion]: required(WEB_VERSION),
    [ENTRY.swVersion]: swVersion,
    fvv: '1',
    pageHistory: '0',
  });

  // 요청을 띄우기 직전에 기억한다. 타임아웃으로 실패 처리돼도 요청 자체는 계속 날아가므로,
  // 다시 누르는 것을 여기서 막아야 시트에 두 줄이 안 생긴다.
  rememberSubmit();

  // URLSearchParams 를 그대로 넘기면 Content-Type 이 application/x-www-form-urlencoded
  // 로 잡힌다 — no-cors 에서 허용되는 몇 안 되는 값.
  // AbortController 는 이 앱이 쓴 적이 없어(구형 WebView) 타임아웃만 경주시킨다.
  // 요청 자체는 계속 날아가지만, 화면이 "보내는 중" 에 갇히지 않으면 된다.
  const request = fetch(FORM_ACTION, { method: 'POST', mode: 'no-cors', body }).catch((error) => {
    // 요청이 아예 못 나간 경우(회선 끊김)만 기억을 지운다 — 이때는 재시도가 맞다.
    forgetSubmit();
    throw error;
  });
  const timeout = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('inquiry submit timeout')), SUBMIT_TIMEOUT_MS);
  });

  await Promise.race([request, timeout]);
}
